import {
  Box,
  type BoxProps,
  Input,
  InputGroup,
  InputLeftElement,
  type InputProps,
} from "@chakra-ui/react";
import { t } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { observer } from "mobx-react-lite";
import { useImperativeHandle, useRef, useState } from "react";
import { useDebounce } from "react-use";

interface FiltersPanelSearchProps extends Omit<BoxProps, "onChange"> {
  onSearch: (val: string) => void;
  searchPlaceholder?: string;
  inputWidth?: InputProps["width"];
  onChange?: (val: string) => void;
  /** Makes the input controlled; pair it with `onChange`. */
  value?: string;
}
export interface FiltersPanelSearchRef {
  setValue: (newValue: string) => void;
}

export const FiltersPanelSearch = observer<
  FiltersPanelSearchProps,
  FiltersPanelSearchRef
>(
  function FiltersPanelSearch(
    {
      onSearch,
      searchPlaceholder,
      inputWidth = "320px",
      onChange,
      value,
      ...props
    },
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [searchTerm, setSearchTerm] = useState<undefined | string>(undefined);

    useImperativeHandle(
      ref,
      () => ({
        setValue: (value) => {
          if (!inputRef.current) return;
          inputRef.current.value = value;
        },
      }),
      [inputRef.current],
    );

    useDebounce(
      () => {
        if (searchTerm !== undefined && !!onSearch) onSearch(searchTerm);
      },
      1000,
      [searchTerm],
    );

    const handleChange = (value: string) => {
      setSearchTerm(value);
      onChange?.(value);
    };

    return (
      <Box {...props}>
        <InputGroup alignItems="center">
          <InputLeftElement h="9" pointerEvents="none">
            <Icon name="search-md" color="gray.600" />
          </InputLeftElement>
          <Input
            ref={inputRef}
            w={inputWidth}
            defaultValue={value === undefined ? props.defaultValue : undefined}
            value={value}
            onChange={({ target }) => handleChange(target.value)}
            placeholder={searchPlaceholder ?? t`Search`}
            rounded="md"
            type="search"
          />
        </InputGroup>
      </Box>
    );
  },
  {
    forwardRef: true,
  },
);
